import { Grid } from '../common/Grid';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, IconButton, Divider, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
  Card, CardContent, Avatar, Tooltip, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  SwapHoriz as SwapIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  LocalPharmacy as MedIcon,
  Assignment as OrderIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { usePharmacyUsers } from '../../hooks/queries/usePharmacyUsers';
import { PatientSearchModal } from '../phox/PatientSearchModal';
import DoctorSelectionDialog from '../common/DoctorSelectionDialog';
import orderService, { UniversalOrderData } from '../../services/orderService';
import { TASK_TYPES, TaskTypeKey, TaskQueueItem, CreateManualTaskDto } from '../../services/taskQueueService';
import {
  TaskDepartment,
  TASK_DEPARTMENT_VALUES,
  TASK_DEPARTMENT_LABELS,
  departmentForTaskType,
} from '../../constants/taskDepartments';
import { getDepartmentAssignableUsers } from '../../utils/assignableUsers';

// ---- Types ----

interface SelectedPatient {
  name: string;
  dob?: string;
  krollId?: string;
  foxId?: string;
  compositeId?: string;
  phone?: string;
  medications?: any[];
}

interface SelectedDoctor {
  id: string;
  name: string;
  cpso?: string;
}

/**
 * GP-1115: what the dialog hands to onSave. Extends the manual-task DTO with
 * counseling-only context (drug linked from the patient's meds, the selected
 * doctor) so the owner can route a Patient Counselling creation through the
 * counseling pipeline instead of a bare manual task.
 */
export interface CreateTaskPayload extends CreateManualTaskDto {
  drugName?: string;
  din?: string;
  doctorName?: string;
}

/**
 * GP-1222: context handed in when the dialog is launched from a place that
 * already knows the patient / order / drug / prescriber — e.g. the Patient 360
 * Order History "Add Counseling Task" row action — so the pharmacist confirms
 * and clicks Create instead of re-searching for everything. Only applied on
 * CREATE (ignored while editing an existing task). All fields optional.
 */
export interface CreateTaskPrefill {
  taskType?: TaskTypeKey;
  patientId?: string;
  patientName?: string;
  patientDob?: string;
  krollId?: string;
  foxId?: string;
  phone?: string;
  rxNumber?: string;
  drugName?: string;
  din?: string;
  orderId?: string;
  orderNumber?: string;
  doctorName?: string;
  /**
   * GP-1263: the Rx's belonging to the linked order, so the Linking section can
   * offer a drop-down (pick from the order) instead of the free-text RX Number
   * search that returned "Order not found". Sourced from the already-loaded
   * order client-side — no universal-search call needed.
   */
  orderRxOptions?: Array<{ rxNumber: string; drugName?: string; din?: string }>;
}

interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (dto: CreateTaskPayload) => Promise<void>;
  editingTask?: TaskQueueItem | null;
  /**
   * GP-1222: pre-fills a NEW task from caller context. Pass a STABLE reference
   * (build it once on the launching click, not inline each render) — the reset
   * effect keys on it, so a fresh object every render would wipe user edits.
   */
  prefill?: CreateTaskPrefill | null;
  saving: boolean;
}

// ---- Helpers ----

const getTaskTypeLabel = (taskType: string | undefined): string => {
  if (!taskType) return '';
  return TASK_TYPES[taskType as TaskTypeKey] || taskType;
};

const extractKrollId = (compositeId?: string): string => {
  if (!compositeId) return '';
  const parts = compositeId.split('_');
  return parts.length > 1 ? parts[1] : compositeId;
};

// ---- Component ----

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  open,
  onClose,
  onSave,
  editingTask,
  prefill,
  saving,
}) => {
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  const { data: pharmacyUsersData } = usePharmacyUsers(activePharmacy?.id);

  // Form state
  const [taskType, setTaskType] = useState('');
  // PRX-67 (D1): defaults from the chosen task type (departmentForTaskType,
  // R2) and is editable by hand — a task can legitimately move teams.
  const [department, setDepartment] = useState<TaskDepartment | ''>('');
  const [troubleshootingReason, setTroubleshootingReason] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [rxNumber, setRxNumber] = useState('');
  // Drug context captured when an Rx is linked from the patient's medication
  // list — travels to the counseling record for Patient Counselling tasks.
  const [linkedMed, setLinkedMed] = useState<{ drugName?: string; din?: string } | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderId, setOrderId] = useState('');
  // GP-1263: Rx's on the linked order. When non-empty the Linking section shows
  // a drop-down of these instead of a free-text RX Number field.
  const [rxOptions, setRxOptions] = useState<Array<{ rxNumber: string; drugName?: string; din?: string }>>([]);

  // Patient state
  const [selectedPatient, setSelectedPatient] = useState<SelectedPatient | null>(null);
  const [patientName, setPatientName] = useState('');
  const [patientDob, setPatientDob] = useState('');
  const [krollId, setKrollId] = useState('');
  const [foxId, setFoxId] = useState('');
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);

  // Doctor state
  const [selectedDoctor, setSelectedDoctor] = useState<SelectedDoctor | null>(null);
  const [doctorSearchOpen, setDoctorSearchOpen] = useState(false);

  // Patient context data
  const [patientOrders, setPatientOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Order search
  const [searchingOrder, setSearchingOrder] = useState(false);
  const [orderSearchError, setOrderSearchError] = useState<string | null>(null);

  // Reset form on open/close
  useEffect(() => {
    if (open) {
      if (editingTask) {
        setTaskType(editingTask.taskType || '');
        // PRX-67 (D1): an existing task's stored department wins over what the
        // type map would suggest — it may have been hand-corrected already.
        setDepartment(editingTask.department ?? departmentForTaskType(editingTask.taskType) ?? '');
        setTroubleshootingReason(editingTask.troubleshootingReason || '');
        setAssignedTo(editingTask.assignedTo || '');
        setNotes(editingTask.description || '');
        setLinkedMed(null);
        setRxNumber(editingTask.rxNumber || '');
        setOrderNumber(editingTask.orderNumber || '');
        setOrderId(editingTask.orderId || '');
        setRxOptions([]);
        setPatientName(editingTask.patient?.name || '');
        setPatientDob(editingTask.patientDob || '');
        setKrollId(editingTask.krollId || '');
        setFoxId(editingTask.foxId || '');
        setSelectedPatient(null);
        setSelectedDoctor(null);
        setPatientOrders([]);
      } else if (prefill) {
        // GP-1222: seed a new Patient Counselling task from caller context
        // (patient linked, Rx/drug linked, prescriber filled) so the pharmacist
        // only confirms and clicks Create.
        setTaskType(prefill.taskType || 'patient_counselling');
        setDepartment(departmentForTaskType(prefill.taskType || 'patient_counselling') ?? '');
        setTroubleshootingReason('');
        setAssignedTo('');
        setNotes('');
        setRxNumber(prefill.rxNumber || '');
        setLinkedMed(prefill.rxNumber ? { drugName: prefill.drugName, din: prefill.din } : null);
        setOrderNumber(prefill.orderNumber || '');
        setOrderId(prefill.orderId || '');
        setRxOptions(prefill.orderRxOptions || []);
        setPatientName(prefill.patientName || '');
        setPatientDob(prefill.patientDob || '');
        setKrollId(prefill.krollId || '');
        setFoxId(prefill.foxId || '');
        setSelectedPatient(
          prefill.patientName
            ? {
                name: prefill.patientName,
                dob: prefill.patientDob,
                krollId: prefill.krollId,
                foxId: prefill.foxId,
                compositeId: prefill.patientId,
                phone: prefill.phone,
                // Kept empty: the caller already linked the Rx, so the meds
                // accordion is unnecessary here (and we have no med list).
                medications: [],
              }
            : null,
        );
        setSelectedDoctor(prefill.doctorName ? { id: '', name: prefill.doctorName } : null);
        setPatientOrders([]);
      } else {
        setTaskType('');
        setDepartment('');
        setTroubleshootingReason('');
        setAssignedTo('');
        setNotes('');
        setLinkedMed(null);
        setRxNumber('');
        setOrderNumber('');
        setOrderId('');
        setRxOptions([]);
        setPatientName('');
        setPatientDob('');
        setKrollId('');
        setFoxId('');
        setSelectedPatient(null);
        setSelectedDoctor(null);
        setPatientOrders([]);
      }
      setOrderSearchError(null);
    }
  }, [open, editingTask, prefill]);

  // Load patient orders when patient is selected
  useEffect(() => {
    if (!selectedPatient?.compositeId || !activePharmacy?.id) return;
    setLoadingOrders(true);
    orderService.getOrders(activePharmacy.id, { patientId: selectedPatient.compositeId, limit: 5 })
      .then((res) => setPatientOrders(res.data || []))
      .catch(() => setPatientOrders([]))
      .finally(() => setLoadingOrders(false));
  }, [selectedPatient?.compositeId, activePharmacy?.id]);

  // ---- Handlers ----

  const handlePatientSelected = (patient: any) => {
    const name = patient.lastName && patient.firstName
      ? `${patient.lastName}, ${patient.firstName}`
      : patient.patientName || patient.name || '';
    const composite = patient.compositeId || patient.id || '';
    const kId = extractKrollId(composite);

    setSelectedPatient({
      name,
      dob: patient.dateOfBirth || patient.dob || '',
      krollId: kId,
      foxId: patient.foxId || '',
      compositeId: composite,
      phone: patient.phone || patient.homePhone || '',
      medications: patient.medications || [],
    });
    setPatientName(name);
    setPatientDob(patient.dateOfBirth || patient.dob || '');
    setKrollId(kId);
    setFoxId(patient.foxId || '');
    setPatientSearchOpen(false);
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setPatientName('');
    setPatientDob('');
    setKrollId('');
    setFoxId('');
    setPatientOrders([]);
  };

  const handleDoctorSelected = (doctor: any) => {
    setSelectedDoctor({
      id: doctor.DoctorID || doctor.id,
      name: `${doctor.DoctorLastName || ''}, ${doctor.DoctorFirstName || ''}`.replace(/(^, |, $)/g, ''),
      cpso: doctor.CPSO || '',
    });
    setDoctorSearchOpen(false);
  };

  const handleOrderSearch = async () => {
    if (!orderNumber.trim() || !activePharmacy?.id) return;
    setSearchingOrder(true);
    setOrderSearchError(null);
    try {
      const result = await orderService.searchUniversal({ query: orderNumber.trim(), type: 'orderId', pharmacyId: activePharmacy.id });
      if (result) {
        setOrderNumber(result.orderNumber);
        setOrderId(result.orderId);
        // Auto-fill patient from order if not already selected
        if (!selectedPatient && result.patient?.name) {
          setPatientName(result.patient.name);
          setPatientDob(result.patient.dateOfBirth || '');
        }
        setOrderSearchError(null);
      } else {
        setOrderSearchError('Order not found');
      }
    } catch {
      setOrderSearchError('Failed to search order');
    } finally {
      setSearchingOrder(false);
    }
  };

  // PatientSearchModal wraps each med as { id, medicationName, strength, ...,
  // metadata: <raw Kroll row> }, and the raw Kroll row uses PascalCase fields
  // (RxNum / BrandName / GenericName / Strength / DIN). The camelCase names the
  // wrapper maps from don't exist on the Kroll test-rx-query payload, so read
  // every shape — otherwise the whole medications panel renders as dashes.
  const medRxNumber = (med: any): string => {
    const v = med.rxNumber ?? med.rxNum ?? med.RxNum ?? med.metadata?.RxNum ?? med.metadata?.rxNumber ?? med.id;
    return v == null ? '' : String(v);
  };
  const medDrugName = (med: any): string | undefined =>
    med.brandName || med.genericName || med.drugName || med.medicationName ||
    med.BrandName || med.GenericName ||
    med.metadata?.BrandName || med.metadata?.GenericName || med.metadata?.drugName || undefined;
  const medStrength = (med: any): string | undefined =>
    med.strength || med.drugStrength || med.Strength || med.metadata?.Strength || undefined;

  const handleLinkMedication = (med: any) => {
    setRxNumber(medRxNumber(med));
    setLinkedMed({
      drugName: medDrugName(med),
      din: med.din || med.DIN || med.metadata?.DIN || med.metadata?.din || undefined,
    });
  };

  // GP-1263: pick an Rx from the linked order's drop-down. Sets the Rx and its
  // drug context from the pre-loaded option — no free-text / universal-search.
  const handleSelectRxOption = (rx: string) => {
    setRxNumber(rx);
    const opt = rxOptions.find((o) => o.rxNumber === rx);
    setLinkedMed(opt ? { drugName: opt.drugName, din: opt.din } : null);
  };

  const handleLinkOrder = (order: any) => {
    setOrderNumber(order.orderNumber || '');
    setOrderId(order.id || '');
  };

  // PRX-67 (D1, R2): changing the task type re-defaults the department from
  // the type map. A hand-picked department is not "sticky" across a further
  // type change here — the spec case is "choosing Transfer Out preselects
  // Logistics" for a fresh selection, and re-deriving on every type change
  // keeps the two from silently drifting apart the way a one-time-only default
  // would after a few back-and-forth clicks.
  const handleTaskTypeChange = (value: string) => {
    setTaskType(value);
    setDepartment(departmentForTaskType(value) ?? '');
  };

  const handleSubmit = () => {
    if (!taskType) return;
    const taskTypeLabel = getTaskTypeLabel(taskType);
    const dto: CreateTaskPayload = {
      title: `${taskTypeLabel}: ${patientName || 'Unknown Patient'}`,
      description: notes || undefined,
      priority: 'high',
      assignedTo: assignedTo || undefined,
      patientName: patientName || undefined,
      patientId: selectedPatient?.compositeId || undefined,
      patientPhone: selectedPatient?.phone || undefined,
      orderId: orderId || undefined,
      orderNumber: orderNumber || undefined,
      entryMethod: 'Manual',
      troubleshootingReason: troubleshootingReason || undefined,
      taskType: taskType || undefined,
      patientDob: patientDob || undefined,
      krollId: krollId || undefined,
      foxId: foxId || undefined,
      linkStatus: (selectedPatient?.compositeId || krollId) ? 'linked' : 'unlinked',
      rxNumber: rxNumber || undefined,
      drugName: linkedMed?.drugName,
      din: linkedMed?.din,
      doctorName: selectedDoctor?.name || undefined,
      department: department || null,
    };
    onSave(dto);
  };

  /**
   * PRX-67 (wave 1 review item 1b, Major) — filter the Assigned To picker
   * through the same department-role gate the server enforces (D7), the way
   * TaskQueueContent's getAssignableUsers already does — this dialog used to
   * offer EVERY pharmacy user regardless of department, including roles
   * create/update now reject (e.g. a logistics driver on a billing task).
   *
   * An already-assigned user stays visible even when their role no longer
   * matches the current department (editing a task whose department was just
   * changed, or a legacy row assigned before this gate existed) — hiding an
   * existing assignment is not this fix's job, only narrowing NEW choices.
   */
  const departmentAssignableUsers = useMemo(
    () => getDepartmentAssignableUsers(pharmacyUsersData?.users, department || null),
    [pharmacyUsersData, department],
  );
  const assignedToOptions = useMemo(() => {
    if (!assignedTo || departmentAssignableUsers.some((u) => u.id === assignedTo)) {
      return departmentAssignableUsers;
    }
    const currentName = pharmacyUsersData?.userIdToNameMap?.get(assignedTo) || assignedTo;
    return [...departmentAssignableUsers, { id: assignedTo, name: currentName }].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [departmentAssignableUsers, assignedTo, pharmacyUsersData]);

  const medications = selectedPatient?.medications || [];

  // GP-1116: while the Patient Search (maxWidth="lg") or Doctor modal is open,
  // hide this dialog instead of stacking behind it. MUI's 50%-opacity backdrop
  // let the wider search modal reveal this dialog's fields and a second "Cancel"
  // button behind it (bleed-through, frames 03/04). The component stays mounted,
  // so all form state persists — closing the child modal restores it intact.
  const childModalOpen = patientSearchOpen || doctorSearchOpen;

  return (
    <>
      <Dialog open={open && !childModalOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
          {editingTask ? 'Edit Task' : 'Create New Task'}
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2.5 }}>
            {/* Section: Task Configuration */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Task Type</InputLabel>
                  <Select value={taskType} onChange={(e) => handleTaskTypeChange(e.target.value)} label="Task Type">
                    {/* GP-1115 held every non-counseling type as a disabled
                        "(coming soon)" MenuItem while the Task Queue served
                        counseling only. GP-1464 opens the queue to the rest:
                        Billing Troubleshooting tasks are created from Billing
                        Item Troubleshooting, and the "Reach out to…" types are
                        the follow-up work those pathways generate. The backend
                        half of the gate is MANUALLY_CREATABLE_TASK_TYPES —
                        keep the two in step. */}
                    {Object.entries(TASK_TYPES).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {/* PRX-67 (D1, R2): defaults from the task type above via
                    departmentForTaskType, editable by hand — a task can
                    legitimately move teams (e.g. a "Reach out to Patient" that
                    turns out to need Logistics). */}
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={department}
                    onChange={(e) => setDepartment((e.target.value || '') as TaskDepartment | '')}
                    label="Department"
                  >
                    <MenuItem value="">Uncategorized</MenuItem>
                    {TASK_DEPARTMENT_VALUES.map((dept) => (
                      <MenuItem key={dept} value={dept}>{TASK_DEPARTMENT_LABELS[dept]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned To</InputLabel>
                  <Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} label="Assigned To">
                    <MenuItem value="">Unassigned</MenuItem>
                    {assignedToOptions.map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Troubleshooting Reason"
                  value={troubleshootingReason} onChange={(e) => setTroubleshootingReason(e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Section: Patient */}
            <Divider sx={{ my: 2 }}>
              <Chip label="Patient" size="small" icon={<PersonIcon />} />
            </Divider>

            {selectedPatient ? (
              /* Patient Card */
              <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontWeight: 700 }}>
                    {selectedPatient.name?.charAt(0)?.toUpperCase() || 'P'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{selectedPatient.name}</Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
                      {selectedPatient.dob && (
                        <Typography variant="caption" color="text.secondary">DOB: {selectedPatient.dob}</Typography>
                      )}
                      {selectedPatient.krollId && (
                        <Typography variant="caption" color="text.secondary">Kroll: {selectedPatient.krollId}</Typography>
                      )}
                      {selectedPatient.phone && (
                        <Typography variant="caption" color="text.secondary">Ph: {selectedPatient.phone}</Typography>
                      )}
                    </Stack>
                  </Box>
                  <Chip label="Linked" size="small" color="success" sx={{ fontWeight: 600 }} />
                  <Tooltip title="Change Patient">
                    <IconButton size="small" onClick={() => setPatientSearchOpen(true)}>
                      <SwapIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear Patient">
                    <IconButton size="small" onClick={handleClearPatient}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardContent>
              </Card>
            ) : (
              /* Patient Search Field */
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth size="small" label="Patient Name"
                    value={patientName} onChange={(e) => setPatientName(e.target.value)}
                    placeholder="Type name or search..."
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="outlined" fullWidth
                    startIcon={<SearchIcon />}
                    onClick={() => setPatientSearchOpen(true)}
                    sx={{ height: 40, textTransform: 'none' }}
                  >
                    Search Patient
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Patient DOB" placeholder="YYYY-MM-DD"
                    value={patientDob} onChange={(e) => setPatientDob(e.target.value)} />
                </Grid>
              </Grid>
            )}

            {/* Active Medications (after patient selection) */}
            {medications.length > 0 && (
              <Accordion sx={{ mb: 1.5, borderRadius: '8px !important', '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                      Active Medications
                    </Typography>
                    <Chip label={medications.length} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Drug</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Rx#</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Strength</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }} align="right"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {medications.slice(0, 8).map((med: any, idx: number) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                              {medDrugName(med) || '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                              {medRxNumber(med) || '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                              {medStrength(med) || '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }} align="right">
                              <Button
                                size="small"
                                startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleLinkMedication(med)}
                                disabled={!!rxNumber && rxNumber === medRxNumber(med)}
                                sx={{ fontSize: '0.7rem', minWidth: 0, textTransform: 'none', py: 0 }}
                              >
                                {!!rxNumber && rxNumber === medRxNumber(med) ? 'Linked' : 'Link'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Recent Orders (after patient selection) */}
            {selectedPatient && (
              <Accordion sx={{ mb: 1.5, borderRadius: '8px !important', '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <OrderIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                      Recent Orders
                    </Typography>
                    {loadingOrders ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Chip label={patientOrders.length} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                  {patientOrders.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Order #</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Stage</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }} align="right"></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {patientOrders.map((order: any, idx: number) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {order.orderNumber || '—'}
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <Chip label={order.status || '—'} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                {order.workflowStage?.replace(/_/g, ' ') || '—'}
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }} align="right">
                                <Button
                                  size="small"
                                  startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => handleLinkOrder(order)}
                                  disabled={orderId === (order.id || '')}
                                  sx={{ fontSize: '0.7rem', minWidth: 0, textTransform: 'none', py: 0 }}
                                >
                                  {orderId === (order.id || '') ? 'Linked' : 'Link'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : !loadingOrders ? (
                    <Typography variant="caption" color="text.disabled" sx={{ py: 1, display: 'block', textAlign: 'center' }}>
                      No recent orders found
                    </Typography>
                  ) : null}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Section: Linking */}
            <Divider sx={{ my: 2 }}>
              <Chip label="Linking" size="small" icon={<LinkIcon />} />
            </Divider>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={5}>
                {/* GP-1263: when the order's Rx's are known (launched from Order
                    History), pick from a drop-down instead of typing/searching a
                    free-text Rx that hit the failing universal-search lookup.
                    Falls back to free text where no order Rx list is supplied
                    (e.g. the Task Queue's manual create). */}
                {rxOptions.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>RX Number</InputLabel>
                    <Select
                      label="RX Number"
                      value={rxNumber}
                      onChange={(e) => handleSelectRxOption(String(e.target.value))}
                      sx={{ '& .MuiSelect-select': { fontFamily: 'monospace' } }}
                    >
                      {rxOptions.map((opt) => (
                        <MenuItem key={opt.rxNumber} value={opt.rxNumber}>
                          {opt.rxNumber}{opt.drugName ? ` — ${opt.drugName}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth size="small" label="RX Number"
                    value={rxNumber}
                    onChange={(e) => { setRxNumber(e.target.value); setLinkedMed(null); }}
                    sx={{ '& input': { fontFamily: 'monospace' } }}
                  />
                )}
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth size="small" label="Order Number"
                  value={orderNumber} onChange={(e) => { setOrderNumber(e.target.value); setOrderSearchError(null); }}
                  error={!!orderSearchError}
                  helperText={orderSearchError}
                  sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  variant="outlined" fullWidth
                  onClick={handleOrderSearch}
                  disabled={!orderNumber.trim() || searchingOrder}
                  sx={{ height: 40 }}
                >
                  {searchingOrder ? <CircularProgress size={18} /> : <SearchIcon />}
                </Button>
              </Grid>
            </Grid>

            {/* Section: Doctor */}
            <Divider sx={{ my: 2 }}>
              <Chip label="Doctor (Optional)" size="small" />
            </Divider>

            {selectedDoctor ? (
              <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, '&:last-child': { pb: 1 } }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36, fontSize: '0.875rem' }}>
                    Dr
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{selectedDoctor.name}</Typography>
                    {selectedDoctor.cpso && (
                      <Typography variant="caption" color="text.secondary">CPSO: {selectedDoctor.cpso}</Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={() => setDoctorSearchOpen(true)}>
                    <SwapIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setSelectedDoctor(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ) : (
              <Button
                variant="outlined" size="small" fullWidth
                startIcon={<SearchIcon />}
                onClick={() => setDoctorSearchOpen(true)}
                sx={{ mb: 2, textTransform: 'none', justifyContent: 'flex-start', color: 'text.secondary' }}
              >
                Select Doctor...
              </Button>
            )}

            {/* Notes */}
            <TextField
              fullWidth label="Notes" multiline rows={2} size="small"
              value={notes} onChange={(e) => setNotes(e.target.value)}
            />

            {/* SLA Info */}
            <Alert severity="info" sx={{ mt: 2, py: 0.5, borderRadius: 1.5 }}>
              Due date will be auto-calculated based on the selected Task Type SLA.
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || !taskType}>
            {saving ? <CircularProgress size={20} /> : editingTask ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient Search Modal (stacked over dialog) */}
      <PatientSearchModal
        open={patientSearchOpen}
        onClose={() => setPatientSearchOpen(false)}
        onSelectPatient={handlePatientSelected}
        title="Search Patient"
        subtitle="Search by last name or 'Last, First' format"
      />

      {/* Doctor Selection Dialog (stacked over dialog) */}
      <DoctorSelectionDialog
        open={doctorSearchOpen}
        onClose={() => setDoctorSearchOpen(false)}
        onSelect={handleDoctorSelected}
        onSkip={() => setDoctorSearchOpen(false)}
        patientId={selectedPatient?.krollId}
      />
    </>
  );
};

export default CreateTaskDialog;
